import React, { useState, useEffect, useCallback } from 'react';
import { userAPI, getCurrentUser } from '../../services/api';
import { User } from '../../types';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Select } from '../common/Select';
import { IconButton } from '../common/IconButton';
import { KeyIcon, TrashIcon, CheckIcon, XIcon } from '../common/icons';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Membre' },
  { value: 'admin', label: 'Administrateur' },
];

interface Props {
  onUsersChanged?: () => void; // families need to refresh their member list
}

const UsersSection: React.FC<Props> = ({ onUsersChanged }) => {
  const me = getCurrentUser();
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [creating, setCreating] = useState(false);

  // per-row password reset
  const [resetId, setResetId] = useState<string | null>(null);
  const [resetPwd, setResetPwd] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(() => {
    userAPI.getAll().then((r) => setUsers(r.data)).catch(() => setError('Erreur de chargement'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      await userAPI.create({ username, password, role });
      setUsername(''); setPassword(''); setRole('member');
      load();
      onUsersChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async (id: string) => {
    if (!resetPwd) return;
    try {
      await userAPI.update(id, { password: resetPwd });
      setResetId(null); setResetPwd('');
    } catch {
      setError('Erreur lors de la réinitialisation');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await userAPI.delete(id);
      setConfirmDelete(null);
      load();
      onUsersChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Create */}
      <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 sm:items-end bg-gray-50 rounded-xl p-4">
        <Input label="Identifiant" value={username} onChange={(e) => setUsername(e.target.value)}
          placeholder="prenom" autoCapitalize="none" required fullWidth />
        <Input label="Mot de passe" type="text" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="mot de passe" required fullWidth />
        <Select label="Rôle" options={ROLE_OPTIONS} value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'member')} fullWidth />
        <Button type="submit" variant="primary" loading={creating}
          disabled={!username || !password} className="w-full sm:w-auto">Créer</Button>
      </form>

      {/* List */}
      <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
        {users.map((u) => (
          <div key={u._id} className="px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="font-medium text-gray-900">{u.username}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
              {u.role === 'admin' ? 'Admin' : 'Membre'}
            </span>
            {me?._id === u._id && <span className="text-xs text-gray-400">(vous)</span>}

            <div className="ml-auto flex items-center gap-2">
              {resetId === u._id ? (
                <>
                  <Input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)}
                    placeholder="nouveau mot de passe" autoFocus className="w-48" />
                  <IconButton type="button" size="sm" variant="success" ariaLabel="Confirmer"
                    onClick={() => handleReset(u._id)} disabled={!resetPwd}><CheckIcon /></IconButton>
                  <IconButton type="button" size="sm" variant="ghost" ariaLabel="Annuler"
                    onClick={() => { setResetId(null); setResetPwd(''); }}><XIcon /></IconButton>
                </>
              ) : confirmDelete === u._id ? (
                <>
                  <span className="text-sm text-gray-500">Supprimer&nbsp;?</span>
                  <IconButton type="button" size="sm" variant="danger" ariaLabel="Confirmer la suppression"
                    onClick={() => handleDelete(u._id)}><CheckIcon /></IconButton>
                  <IconButton type="button" size="sm" variant="ghost" ariaLabel="Annuler"
                    onClick={() => setConfirmDelete(null)}><XIcon /></IconButton>
                </>
              ) : (
                <>
                  <IconButton type="button" size="sm" variant="ghost" ariaLabel="Réinitialiser le mot de passe"
                    title="Réinitialiser le mot de passe" onClick={() => { setResetId(u._id); setResetPwd(''); }}><KeyIcon /></IconButton>
                  {me?._id !== u._id && (
                    <IconButton type="button" size="sm" variant="ghost" ariaLabel="Supprimer le compte"
                      title="Supprimer le compte" onClick={() => setConfirmDelete(u._id)}
                      className="hover:!bg-danger/10 hover:!text-danger"><TrashIcon /></IconButton>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsersSection;
